import Vue from "vue";
import Vuex from "vuex";
import AnalyticsIntegration from "../apis/AnalyticsIntegration";

Vue.use(Vuex);

function getDefaultState() {
  return {
    tenantBasicInfo: null,
    tenantUsers: [],
    tenantAdminOwner: null,
    tenantBranches: [],
    tenantFeatureUsage: null,
    tenantActivityLog: []
  };
}

function showErrorToast(defaultMessage, error) {
  const toast = Vue.prototype.$toasted;
  if (!toast) {
    return;
  }

  const message =
    error?.response?.data?.message || error?.message || defaultMessage;

  toast.error(message || defaultMessage);
}

export default {
  namespaced: true,
  state: getDefaultState,
  getters: {
    tenantBasicInfo: (state) => state.tenantBasicInfo,
    tenantUsers: (state) => state.tenantUsers,
    tenantAdminOwner: (state) => state.tenantAdminOwner,
    tenantBranches: (state) => state.tenantBranches,
    tenantFeatureUsage: (state) => state.tenantFeatureUsage,
    tenantActivityLog: (state) => state.tenantActivityLog,
  },
  mutations: {
    SET_TENANT_BASIC_INFO(state, info) {
      state.tenantBasicInfo = info;
    },
    SET_TENANT_USERS(state, users) {
      state.tenantUsers = Array.isArray(users) ? users : [];
      state.tenantAdminOwner =
        state.tenantUsers.find(user => Number(user.is_owner) === 1) || null;
    },
    SET_TENANT_BRANCHES(state, branches) {
      state.tenantBranches = Array.isArray(branches) ? branches : [];
    },
    SET_TENANT_FEATURE_USAGE(state, usage) {
      state.tenantFeatureUsage = usage;
    },
    SET_TENANT_ACTIVITY_LOG(state, payload) {
      state.tenantActivityLog = payload;
    },
    RESET_STATE(state) {
      Object.assign(state, getDefaultState());
    }
  },
  actions: {
    async fetchTenantBasicInfo({ commit }, tenantId) {
      commit("RESET_STATE");

      try {
        const tenantResponse = await AnalyticsIntegration.getTenantBasicInfo(
          tenantId
        );
        const info = tenantResponse?.data ?? tenantResponse ?? null;
        commit("SET_TENANT_BASIC_INFO", info);
        return info;
      } catch (error) {
        showErrorToast("Failed to fetch tenant basic info.", error);
        throw error;
      }
    },
    async fetchTenantUsers({ commit }, payload) {
      try {
        let tenantId = null;
        let filters = undefined;

        if (typeof payload === "string" || typeof payload === "number") {
          tenantId = payload;
        } else if (payload && typeof payload === "object") {
          tenantId = payload.tenantId;
          filters = payload.filters;
        }

        const response = await AnalyticsIntegration.getTenantUsers(tenantId, filters);
        const users = response?.data ?? response ?? [];
        const hasActiveFilters = Boolean(filters && Object.keys(filters).length);
        if (!hasActiveFilters) {
          commit("SET_TENANT_USERS", users);
        }
        return users;
      } catch (error) {
        showErrorToast("Failed to fetch tenant users.", error);
        throw error;
      }
    },
    async fetchTenantBranches({ commit }, tenantId) {
      try {
        const response = await AnalyticsIntegration.getTenantBranches(tenantId);
        const branches = response?.data ?? response ?? [];
        commit("SET_TENANT_BRANCHES", branches);
        return branches;
      } catch (error) {
        showErrorToast("Failed to fetch tenant branches.", error);
        throw error;
      }
    },
    async fetchTenantFeatureUsage({ commit }, { tenantId, filters } = {}) {
      try {
        const response = await AnalyticsIntegration.getTenantFeatureUsageData(
          tenantId,
          filters
        );
        const usage = response?.data ?? response ?? null;
        commit("SET_TENANT_FEATURE_USAGE", usage);
        return usage;
      } catch (error) {
        showErrorToast("Failed to fetch feature usage data.", error);
        throw error;
      }
    },
    async updatePotentialUsers(_, { tenantId, potentialUsers }) {
      try {
        const response = await AnalyticsIntegration.updatePotentialUsers(
          tenantId,
          potentialUsers
        );
        return response;
      } catch (error) {
        showErrorToast("Failed to update potential users.", error);
        throw error;
      }
    },
    async fetchTenantActivityLog({ commit }, { tenantId, filters } = {}) {
      try {
        const response = await AnalyticsIntegration.getTenantActivityLog(
          tenantId,
          filters
        );
        const payload = response ?? {};
        commit("SET_TENANT_ACTIVITY_LOG", payload);
        return payload;
      } catch (error) {
        showErrorToast("Failed to fetch tenant activity log.", error);
        throw error;
      }
    }
  }
};